import { useState, useEffect } from "react";
import "@/App.css";
import {
  Phone,
  Mail,
  MapPin,
  TreePine,
  Shield,
  Sparkles,
  AlertTriangle,
  Flame,
  Trees,
  Ship,
  ChevronDown,
  Check,
  ArrowRight,
  Menu,
  X,
  Clock,
  Link as LinkIcon,
  Quote,
} from "lucide-react";

/* -------------------- Fallback content (matches /content/site.json) --------------------
   The site always renders instantly from these defaults so LCP / SEO stays fast.
   When /content/site.json is fetched, values are merged over these defaults so any
   edits made through /admin (Decap CMS) become visible after the next page load. */
const FALLBACK = {
  companyName: "Pihametsuri Pirkanmaa",
  seo: {
    title: "Pihapuiden kaato Pirkanmaalla | Pihametsuri Pirkanmaa",
    description:
      "Pihametsuri Pirkanmaa tekee pihapuiden kaadot, ongelmapuiden poistot ja myrskytuhojen raivaukset Lempäälästä koko Pirkanmaan alueelle. Maksuton arviokäynti.",
  },
  heroEyebrow: "Pihapuiden kaadot · Lempäälä",
  heroTitle: "Pihapuiden kaatoa Pirkanmaalla",
  heroTitleAccent: "Pirkanmaalla",
  heroSubtitle:
    "Turvallisesti, huolellisesti ja luotettavasti — Lempäälästä koko Pirkanmaan alueelle.",
  heroText:
    "Pihametsuri Pirkanmaa auttaa pihapuiden kaadoissa, ongelmapuiden poistossa ja myrskytuhojen raivauksessa. Jokainen kohde arvioidaan tapauskohtaisesti, jotta työ voidaan toteuttaa turvallisesti ihmisille, rakennuksille ja ympäristölle.",
  heroImage: "/images/pihametsuri-pirkanmaa-moottorisaha.webp",
  heroImageMobile: "/images/pihametsuri-pirkanmaa-moottorisaha-800.webp",
  heroImageFallback: "/images/pihametsuri-pirkanmaa-moottorisaha.jpg",
  heroImageAlt: "Pihametsuri suojavarusteissa moottorisahan kanssa Pirkanmaalla",
  primaryButton: { text: "Soita 045 123 7097", url: "tel:+358451237097" },
  secondaryButton: { text: "Pyydä maksuton arvio", url: "#yhteystiedot" },
  trustBadges: [
    "Maksuton arviokäynti",
    "Vastuuvakuutettu toiminta",
    "Kotitalousvähennys työn osuudesta",
  ],
  servicesTitle: "Puunkaatopalvelut pihoille ja tonteille",
  servicesLead:
    "Yhden ihmisen paikallinen palvelu — jokainen kohde suunnitellaan ja toteutetaan yksilöllisesti.",
  services: [
    {
      title: "Pihapuiden kaadot",
      description:
        "Pihapuiden kaadot suunnitellaan kohteen mukaan. Huomioon otetaan rakennukset, kulkureitit, sähkölinjat ja muu piha-alue.",
    },
    {
      title: "Ongelmapuiden poisto",
      description:
        "Kallistuneet, huonokuntoiset tai hankalassa paikassa kasvavat puut poistetaan hallitusti ja turvallisesti.",
    },
    {
      title: "Myrskytuhojen raivaus",
      description:
        "Myrskyn kaatamat tai vaurioittamat puut raivataan niin, että piha ja kulkuväylät saadaan jälleen turvallisiksi.",
    },
    {
      title: "Puiden katkominen polttopuiksi",
      description:
        "Kaadetut puut voidaan katkoa sovittuun mittaan esimerkiksi polttopuukäyttöä varten.",
    },
    {
      title: "Pihojen ja tonttien raivaus",
      description:
        "Raivaustyöt auttavat pitämään pihan, tontin ja kulkuväylät siisteinä ja turvallisina.",
    },
    {
      title: "Saarikohteet Näsijärvellä",
      description:
        "Puunkaadot onnistuvat myös Näsijärven saarikohteissa veneellä tai talvella jääolosuhteiden salliessa.",
    },
  ],
  stepsTitle: "Kolme selkeää vaihetta — ei yllätyksiä.",
  steps: [
    { title: "Ota yhteyttä", description: "Soita tai lähetä viesti ja kerro lyhyesti kohteesta." },
    { title: "Maksuton arviokäynti", description: "Kohde arvioidaan paikan päällä ja saat selkeän suunnitelman työn toteutuksesta." },
    { title: "Turvallinen toteutus", description: "Työ tehdään huolellisesti, omaisuutta ja ympäristöä kunnioittaen." },
  ],
  about: {
    title: "Tietoa yrittäjästä",
    chips: ["Lempäälä", "Vastuuvakuutettu", "Metsäalan ammattilainen"],
    paragraphs: [
      "Olen pirkanmaalainen metsäalan ammattilainen ja pihapuiden kaatoihin erikoistunut yrittäjä. Työssäni tärkeintä ovat turvallisuus, huolellinen suunnittelu ja asiakkaan omaisuuden suojaaminen. Jokainen kohde arvioidaan yksilöllisesti, jotta työ voidaan toteuttaa turvallisesti ja hallitusti.",
      "Kehitän osaamistani jatkuvasti kouluttautumalla sekä panostamalla laadukkaisiin työvälineisiin. Tavoitteeni on tarjota palvelua, johon asiakkaan on helppo luottaa.",
    ],
  },
  areaTitle: "Toiminta-alueena koko Pirkanmaa",
  areaText:
    "Palvelen Lempäälästä käsin koko Pirkanmaan alueella. Ota yhteyttä, niin kerron enemmän oman kohteesi mahdollisuuksista.",
  areas: [
    "Lempäälä",
    "Tampere",
    "Pirkkala",
    "Nokia",
    "Kangasala",
    "Valkeakoski",
    "Ylöjärvi",
    "Vesilahti",
    "Akaa",
    "muu Pirkanmaa sopimuksen mukaan",
  ],
  faq: [
    { question: "Onko arviokäynti maksuton?", answer: "Kyllä, arviokäynti on maksuton." },
    { question: "Millä alueella Pihametsuri Pirkanmaa toimii?", answer: "Toiminta-alueena on koko Pirkanmaa. Kotipaikka on Lempäälä." },
    { question: "Voiko työn osuudesta saada kotitalousvähennyksen?", answer: "Työn osuudesta voi olla mahdollista saada kotitalousvähennys voimassa olevan lainsäädännön mukaisesti." },
    { question: "Hoituuko puunkaato saaressa?", answer: "Kyllä, Näsijärven saarikohteisiin voidaan tulla veneellä avovesikaudella ja tarvittaessa talvella jääolosuhteiden salliessa." },
  ],
  contact: {
    phone: "045 123 7097",
    phoneUrl: "tel:+358451237097",
    email: "info@pihametsuripirkanmaa.fi",
    address: "Lempäälä",
    areaServed: "koko Pirkanmaa",
    vatId: "3317714-4",
  },
  openingHours: [],
  socialLinks: [],
  projects: [],
  testimonials: [],
  quoteForm: {
    title: "Tarjouspyyntö",
    lead: "Täytä lomake ja liitä halutessasi mukaan kuvia kohteesta. Kuvat auttavat arvioimaan työn laajuutta.",
    jotformId: "261856431732055",
  },
};

/* Deep-merge helper: content from site.json overrides fallback per key. Arrays are replaced
   entirely (so editors can add/remove items). Missing/empty values keep the fallback. */
function mergeContent(base, override) {
  if (override === undefined || override === null || override === "") return base;
  if (Array.isArray(base) && Array.isArray(override)) return override;
  if (typeof base === "object" && !Array.isArray(base) && typeof override === "object") {
    const out = { ...base };
    for (const k of Object.keys(override)) {
      out[k] = mergeContent(base[k], override[k]);
    }
    return out;
  }
  return override;
}

/* -------------------- Nav items (structural, not editable content) -------------------- */
const NAV_ITEMS = [
  { href: "#palvelut", label: "Palvelut" },
  { href: "#miten-etenee", label: "Miten työ etenee" },
  { href: "#yrittajasta", label: "Yrittäjästä" },
  { href: "#toiminta-alue", label: "Toiminta-alue" },
  { href: "#usein-kysytyt", label: "UKK" },
  { href: "#yhteystiedot", label: "Yhteystiedot" },
];

const SERVICE_ICONS = [TreePine, AlertTriangle, Sparkles, Flame, Trees, Ship];

/* Compose the human-readable title with an accent word rendered in italic display serif.
   If the accent word is not present in the title, the whole title is shown normally. */
function renderTitleWithAccent(title, accent, italicClass) {
  if (!title) return null;
  if (!accent) return title;
  const idx = title.toLowerCase().indexOf(String(accent).toLowerCase());
  if (idx < 0) return title;
  const before = title.slice(0, idx);
  const match = title.slice(idx, idx + accent.length);
  const after = title.slice(idx + accent.length);
  return (
    <>
      {before}
      <span className={italicClass}>{match}</span>
      {after}
    </>
  );
}

function buildMailto(email, subject) {
  return `mailto:${email || ""}?subject=${encodeURIComponent(subject)}`;
}

/* -------------------- Header -------------------- */
function Header({ content }) {
  const [open, setOpen] = useState(false);
  const phone = content.contact?.phone;
  const phoneUrl = content.contact?.phoneUrl;
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-[hsl(38_20%_88%)] bg-[hsl(38_30%_97%)]/85 backdrop-blur-md"
    >
      <div className="container-page flex h-16 items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group" aria-label={`${content.companyName} etusivu`}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(145_32%_18%)] text-[hsl(38_30%_95%)] transition-transform group-hover:-rotate-6">
            <TreePine className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[15px] font-bold text-[hsl(145_32%_14%)]">Pihametsuri</span>
            <span className="text-[11px] font-medium tracking-wider text-[hsl(22_88%_42%)] uppercase">Pirkanmaa</span>
          </span>
        </a>

        <nav aria-label="Päänavigaatio" className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <a key={item.href} href={item.href} data-testid={`nav-link-${item.href.replace("#", "")}`} className="text-sm font-medium text-[hsl(145_15%_28%)] hover:text-[hsl(22_88%_42%)] transition-colors">
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {phone && (
            <a href={phoneUrl} data-testid="header-call-btn" className="flex items-center gap-2 text-sm font-semibold text-[hsl(145_32%_14%)] hover:text-[hsl(22_88%_42%)] transition-colors">
              <Phone className="h-4 w-4" />
              {phone}
            </a>
          )}
          <a href="#yhteystiedot" data-testid="header-cta-btn" className="btn-primary !py-2.5 !text-sm">
            Pyydä arvio
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden inline-flex items-center justify-center rounded-lg p-2 text-[hsl(145_32%_14%)] hover:bg-[hsl(38_20%_92%)]" aria-label="Avaa navigaatio" aria-expanded={open} data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[hsl(38_20%_88%)] bg-[hsl(38_30%_97%)]">
          <nav aria-label="Mobiilinavigaatio" className="container-page py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${item.href.replace("#", "")}`} className="py-2.5 text-base font-medium text-[hsl(145_25%_18%)]">
                {item.label}
              </a>
            ))}
            {phone && (
              <a href={phoneUrl} onClick={() => setOpen(false)} className="mt-2 flex items-center gap-2 text-base font-semibold text-[hsl(22_88%_42%)]" data-testid="mobile-menu-call">
                <Phone className="h-4 w-4" /> {phone}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

/* -------------------- Hero -------------------- */
function Hero({ content }) {
  const italic = "font-serif-display italic font-normal text-[hsl(145_35%_25%)]";
  const primary = content.primaryButton || {};
  const secondary = content.secondaryButton || {};
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[hsl(38_30%_97%)]" aria-hidden="true" />
      <div className="absolute inset-0 -z-10 bg-grain opacity-70" aria-hidden="true" />
      <div className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-[hsl(145_35%_88%)] blur-3xl opacity-60 -z-10" aria-hidden="true" />

      <div className="container-page pt-14 sm:pt-20 lg:pt-24 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
          <div className="max-w-xl">
            {content.heroEyebrow && (
              <div className="mb-5 flex items-center gap-2">
                <span className="section-eyebrow">{content.heroEyebrow}</span>
              </div>
            )}

            <h1 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-bold text-[hsl(145_32%_14%)] leading-[1.02] tracking-tight">
              {renderTitleWithAccent(content.heroTitle, content.heroTitleAccent, italic)}
              <span aria-hidden="true">.</span>
            </h1>

            {content.heroSubtitle && (
              <p className="mt-5 text-lg sm:text-xl text-[hsl(145_15%_26%)] leading-relaxed">
                {content.heroSubtitle}
              </p>
            )}
            {content.heroText && <p className="mt-4 prose-lead">{content.heroText}</p>}

            <div className="mt-8 flex flex-wrap gap-3">
              {primary.text && (
                <a href={primary.url || "#"} data-testid="hero-call-btn" className="btn-primary">
                  <Phone className="h-4 w-4" />
                  {primary.text}
                </a>
              )}
              {secondary.text && (
                <a href={secondary.url || "#"} data-testid="hero-quote-btn" className="btn-secondary">
                  {secondary.text}
                  <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>

            {content.trustBadges?.length > 0 && (
              <ul className="mt-8 flex flex-wrap gap-2.5" aria-label="Luottamustekijät">
                {content.trustBadges.map((b, i) => {
                  const text = typeof b === "string" ? b : b?.text;
                  if (!text) return null;
                  const isShield = /vakuut/i.test(text);
                  return (
                    <li key={i} className="chip">
                      {isShield ? (
                        <Shield className="h-3.5 w-3.5 text-[hsl(145_32%_25%)]" />
                      ) : (
                        <Check className="h-3.5 w-3.5 text-[hsl(22_88%_46%)]" strokeWidth={3} />
                      )}
                      {text}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="relative">
            <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] max-w-md mx-auto lg:mx-0 lg:ml-auto rounded-[28px] overflow-hidden hero-frame bg-[hsl(145_30%_20%)]">
              <picture>
                {content.heroImageMobile && (
                  <source media="(max-width: 640px)" srcSet={content.heroImageMobile} type="image/webp" />
                )}
                {content.heroImage && content.heroImage.endsWith(".webp") && (
                  <source srcSet={content.heroImage} type="image/webp" />
                )}
                <img
                  src={content.heroImageFallback || content.heroImage}
                  alt={content.heroImageAlt || ""}
                  width="1041"
                  height="1608"
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover"
                  data-testid="hero-image"
                />
              </picture>
              {content.contact?.phone && (
                <div className="absolute left-4 bottom-4 sm:left-5 sm:bottom-5 rounded-2xl bg-[hsl(38_30%_97%)]/95 backdrop-blur px-4 py-3 shadow-lg max-w-[80%]">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(22_88%_52%)] text-white">
                      <Phone className="h-4 w-4" />
                    </span>
                    <div className="leading-tight">
                      <div className="text-[11px] font-medium tracking-wider uppercase text-[hsl(145_15%_35%)]">Soita suoraan</div>
                      <a href={content.contact.phoneUrl || "#"} className="text-sm font-bold text-[hsl(145_32%_14%)]">
                        {content.contact.phone}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Services -------------------- */
function Services({ content }) {
  return (
    <section id="palvelut" data-testid="services-section" className="border-t border-[hsl(38_20%_88%)] bg-white">
      <div className="container-page py-16 sm:py-24">
        <div className="max-w-2xl">
          <span className="section-eyebrow">Palvelut</span>
          <h2 className="mt-3 h-section">
            {renderTitleWithAccent(
              content.servicesTitle,
              content.servicesTitle?.split(" ").slice(-3).join(" "),
              "font-serif-display italic font-normal text-[hsl(145_35%_25%)]"
            )}
          </h2>
          {content.servicesLead && <p className="mt-5 prose-lead">{content.servicesLead}</p>}
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(content.services || []).map((s, i) => {
            const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
            return (
              <article key={i} data-testid={`service-card-${i}`} className="card-service">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(145_32%_18%)] text-[hsl(38_30%_95%)]">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <h3 className="mt-4 text-lg font-bold text-[hsl(145_32%_14%)] leading-snug">{s.title}</h3>
                {s.description && (
                  <p className="mt-2 text-sm sm:text-[15px] leading-relaxed text-[hsl(145_12%_32%)]">{s.description}</p>
                )}
                {s.price && (
                  <p className="mt-3 text-sm font-semibold text-[hsl(22_88%_42%)]">{s.price}</p>
                )}
                {s.image && (
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="mt-4 w-full h-40 object-cover rounded-xl"
                  />
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Steps -------------------- */
function Steps({ content }) {
  return (
    <section id="miten-etenee" data-testid="steps-section" className="relative overflow-hidden bg-[hsl(148_40%_12%)] text-[hsl(38_30%_95%)]">
      <div className="absolute inset-0 bg-grain opacity-20" aria-hidden="true" />
      <div className="container-page py-16 sm:py-24 relative">
        <div className="max-w-2xl">
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(22_88%_65%)]">Miten työ etenee</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05]">
            {renderTitleWithAccent(
              content.stepsTitle,
              "ei yllätyksiä",
              "font-serif-display italic font-normal text-[hsl(38_30%_88%)]"
            )}
          </h2>
        </div>

        <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-8">
          {(content.steps || []).map((step, i) => (
            <li key={i} data-testid={`step-${i + 1}`} className="relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
              <div className="step-num">{i + 1}</div>
              <h3 className="mt-5 text-xl font-bold">{step.title}</h3>
              {step.description && (
                <p className="mt-2 text-[15px] leading-relaxed text-[hsl(38_20%_82%)]">{step.description}</p>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* -------------------- About -------------------- */
function About({ content }) {
  const about = content.about || {};
  return (
    <section id="yrittajasta" data-testid="about-section" className="bg-[hsl(38_30%_97%)]">
      <div className="container-page py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
          <div>
            <span className="section-eyebrow">Yrittäjästä</span>
            <h2 className="mt-3 h-section">
              {renderTitleWithAccent(
                about.title,
                about.title?.split(" ").slice(-1)[0],
                "font-serif-display italic font-normal text-[hsl(145_35%_25%)]"
              )}
            </h2>
            {about.chips?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {about.chips.map((c, i) => {
                  const text = typeof c === "string" ? c : c?.text;
                  if (!text) return null;
                  const Icon = /vakuut/i.test(text) ? Shield : /mets/i.test(text) ? TreePine : MapPin;
                  return (
                    <span key={i} className="chip">
                      <Icon className="h-3.5 w-3.5 text-[hsl(145_32%_25%)]" />
                      {text}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="prose-lead space-y-5 text-[hsl(145_15%_22%)]">
            {(about.paragraphs || []).map((p, i) => {
              const text = typeof p === "string" ? p : p?.text;
              return text ? <p key={i}>{text}</p> : null;
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Area -------------------- */
function Area({ content }) {
  const mapsUrl = "https://www.google.com/maps/search/?api=1&query=Lemp%C3%A4%C3%A4l%C3%A4%2C+Pirkanmaa";
  return (
    <section id="toiminta-alue" data-testid="area-section" className="border-t border-[hsl(38_20%_88%)] bg-white">
      <div className="container-page py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
          <div>
            <span className="section-eyebrow">Toiminta-alue</span>
            <h2 className="mt-3 h-section">
              {renderTitleWithAccent(
                content.areaTitle,
                "koko Pirkanmaa",
                "font-serif-display italic font-normal text-[hsl(145_35%_25%)]"
              )}
            </h2>
            {content.areaText && <p className="mt-5 prose-lead">{content.areaText}</p>}
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="area-maps-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[hsl(22_88%_42%)] hover:text-[hsl(22_88%_36%)]">
              Avaa Google Mapsissa
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {(content.areas || []).map((a, i) => {
              const name = typeof a === "string" ? a : a?.name;
              if (!name) return null;
              return (
                <li key={i} data-testid={`area-item-${name}`} className="flex items-center gap-2.5 rounded-xl border border-[hsl(38_20%_88%)] bg-[hsl(38_30%_97%)] px-3.5 py-3 text-sm font-medium text-[hsl(145_25%_18%)]">
                  <MapPin className="h-4 w-4 text-[hsl(145_35%_25%)] shrink-0" />
                  {name}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------- FAQ -------------------- */
function Faq({ content }) {
  const [open, setOpen] = useState(0);
  const faqs = content.faq || [];
  if (!faqs.length) return null;
  return (
    <section id="usein-kysytyt" data-testid="faq-section" className="bg-[hsl(38_30%_97%)] border-t border-[hsl(38_20%_88%)]">
      <div className="container-page py-16 sm:py-24">
        <div className="max-w-2xl">
          <span className="section-eyebrow">Usein kysytyt kysymykset</span>
          <h2 className="mt-3 h-section">
            Vastauksia yleisimpiin{" "}
            <span className="font-serif-display italic font-normal text-[hsl(145_35%_25%)]">
              kysymyksiin
            </span>
          </h2>
        </div>

        <div className="mt-10 max-w-3xl divide-y divide-[hsl(38_20%_88%)] border-y border-[hsl(38_20%_88%)] bg-white rounded-2xl overflow-hidden">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left" aria-expanded={isOpen} data-testid={`faq-toggle-${i}`}>
                  <span className="text-base sm:text-lg font-semibold text-[hsl(145_32%_14%)]">{f.question}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-[hsl(145_25%_25%)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 sm:px-6 pb-5 -mt-1 text-[15px] leading-relaxed text-[hsl(145_12%_32%)]">
                    {f.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Projects (toteutuneet työt) -------------------- */
function Projects({ content }) {
  const items = content.projects || [];
  if (!items.length) return null;
  return (
    <section id="toteutuneet-tyot" data-testid="projects-section" className="bg-white border-t border-[hsl(38_20%_88%)]">
      <div className="container-page py-16 sm:py-24">
        <span className="section-eyebrow">Toteutuneet työt</span>
        <h2 className="mt-3 h-section">
          Esimerkkejä{" "}
          <span className="font-serif-display italic font-normal text-[hsl(145_35%_25%)]">
            tehdyistä töistä
          </span>
        </h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p, i) => (
            <article key={i} data-testid={`project-card-${i}`} className="rounded-2xl overflow-hidden border border-[hsl(38_20%_88%)] bg-[hsl(38_30%_97%)]">
              {p.image && (
                <img
                  src={p.image}
                  alt={p.alt || p.title || "Toteutunut työ"}
                  loading="lazy"
                  className="w-full h-52 object-cover"
                />
              )}
              <div className="p-5">
                {p.title && (
                  <h3 className="text-lg font-bold text-[hsl(145_32%_14%)] leading-snug">{p.title}</h3>
                )}
                {p.description && (
                  <p className="mt-2 text-sm sm:text-[15px] leading-relaxed text-[hsl(145_12%_32%)]">{p.description}</p>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Testimonials (renders only if any) -------------------- */
function Testimonials({ content }) {
  const items = content.testimonials || [];
  if (!items.length) return null;
  return (
    <section id="asiakasarviot" data-testid="testimonials-section" className="bg-white border-t border-[hsl(38_20%_88%)]">
      <div className="container-page py-16 sm:py-24">
        <span className="section-eyebrow">Asiakasarviot</span>
        <h2 className="mt-3 h-section">
          Mitä asiakkaat{" "}
          <span className="font-serif-display italic font-normal text-[hsl(145_35%_25%)]">
            sanovat
          </span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <blockquote key={i} className="rounded-2xl border border-[hsl(38_20%_88%)] bg-[hsl(38_30%_97%)] p-6">
              <Quote className="h-5 w-5 text-[hsl(22_88%_52%)]" />
              <p className="mt-3 text-[15px] leading-relaxed text-[hsl(145_15%_22%)]">{t.quote}</p>
              <footer className="mt-4 text-sm font-semibold text-[hsl(145_32%_14%)]">
                {t.author}
                {t.location && <span className="ml-1 font-normal text-[hsl(145_12%_40%)]">— {t.location}</span>}
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Jotform embed (iframe + dynaaminen embed-handler) -------------------- */
function JotformEmbed({ formId, testid }) {
  useEffect(() => {
    if (!formId) return undefined;
    const HANDLER_SRC = "https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js";
    const selector = `iframe[id='JotFormIFrame-${formId}']`;

    const runHandler = () => {
      if (typeof window !== "undefined" && typeof window.jotformEmbedHandler === "function") {
        try {
          window.jotformEmbedHandler(selector, "https://form.jotform.com/");
        } catch (_) { /* noop */ }
      }
    };

    // Jos handler on jo ladattu (esim. reitin vaihdon jälkeen), aja uudestaan
    if (typeof window !== "undefined" && typeof window.jotformEmbedHandler === "function") {
      runHandler();
      return undefined;
    }

    // Etsi olemassa oleva script, tai lisää uusi
    let script = document.querySelector(`script[src='${HANDLER_SRC}']`);
    let addedByUs = false;
    if (!script) {
      script = document.createElement("script");
      script.src = HANDLER_SRC;
      script.async = true;
      addedByUs = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", runHandler);

    return () => {
      script.removeEventListener("load", runHandler);
      if (addedByUs && script.parentNode) {
        // Jätä script paikoilleen, koska Jotformin handler asentaa globaalin postMessage-kuuntelijan
        // eikä sitä ole tarpeen poistaa remountissa. Ei siivota tässä.
      }
    };
  }, [formId]);

  if (!formId) return null;

  return (
    <div className="jotform-wrapper w-full max-w-full overflow-hidden" data-testid={testid}>
      <iframe
        id={`JotFormIFrame-${formId}`}
        title="Tarjouspyyntölomake"
        src={`https://form.jotform.com/${formId}`}
        allow="geolocation; microphone; camera; fullscreen; payment"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        style={{
          minWidth: "100%",
          maxWidth: "100%",
          width: "100%",
          height: "900px",
          border: "none",
          display: "block",
        }}
        data-testid={`jotform-iframe-${formId}`}
        onLoad={() => {
          try {
            if (typeof window !== "undefined" && typeof window.jotformEmbedHandler === "function") {
              window.jotformEmbedHandler(
                `iframe[id='JotFormIFrame-${formId}']`,
                "https://form.jotform.com/"
              );
            }
          } catch (_) { /* noop */ }
        }}
      />
      <noscript>
        <p>
          Selaimessasi ei ole JavaScriptiä käytössä. Voit avata tarjouspyyntölomakkeen{" "}
          <a href={`https://form.jotform.com/${formId}`}>tästä linkistä</a>.
        </p>
      </noscript>
    </div>
  );
}

/* -------------------- Contact + Jotform -------------------- */
function Contact({ content }) {
  const c = content.contact || {};
  const q = content.quoteForm || {};
  const formId = q.jotformId || "261856431732055";

  return (
    <section id="yhteystiedot" data-testid="contact-section" className="relative bg-[hsl(148_40%_12%)] text-[hsl(38_30%_95%)] border-t border-[hsl(145_25%_20%)]">
      <div className="absolute inset-0 bg-grain opacity-25" aria-hidden="true" />
      <div className="mx-auto w-full max-w-[80rem] px-5 sm:px-8 py-16 sm:py-24 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,0.75fr)_minmax(560px,1.25fr)] gap-10 lg:gap-14">
          <div>
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(22_88%_65%)]">Yhteystiedot</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05]">
              Pyydä{" "}
              <span className="font-serif-display italic font-normal text-[hsl(38_30%_88%)]">maksuton arvio</span>
            </h2>
            <p className="mt-5 text-lg text-[hsl(38_20%_82%)] leading-relaxed max-w-md">
              Kerro lyhyesti kohteesta — vastaan mahdollisimman pian. Arviokäynti ei maksa mitään.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {c.phoneUrl && (
                <a href={c.phoneUrl} data-testid="contact-call-btn" className="btn-primary">
                  <Phone className="h-4 w-4" /> Soita nyt
                </a>
              )}
              {c.email && (
                <a href={buildMailto(c.email, "Tarjouspyyntö pihapuiden kaadosta")} data-testid="contact-email-btn" className="btn-ghost-dark">
                  <Mail className="h-4 w-4" /> Lähetä sähköposti
                </a>
              )}
            </div>

            <dl className="mt-10 space-y-4 text-[15px]">
              {c.phone && <ContactRow icon={<Phone className="h-4 w-4" />} label="Puhelin" value={c.phone} href={c.phoneUrl} testid="info-phone" />}
              {c.email && <ContactRow icon={<Mail className="h-4 w-4" />} label="Sähköposti" value={c.email} href={`mailto:${c.email}`} testid="info-email" />}
              {c.address && <ContactRow icon={<MapPin className="h-4 w-4" />} label="Kotipaikka" value={c.address} testid="info-location" />}
              {c.areaServed && <ContactRow icon={<Trees className="h-4 w-4" />} label="Toiminta-alue" value={c.areaServed} testid="info-area" />}
              {c.vatId && <ContactRow icon={<Shield className="h-4 w-4" />} label="Y-tunnus" value={c.vatId} testid="info-vatid" />}
            </dl>

            {content.openingHours?.length > 0 && (
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-center gap-2 text-[hsl(38_30%_92%)] font-semibold">
                  <Clock className="h-4 w-4" /> Aukioloajat
                </div>
                <ul className="mt-3 text-sm space-y-1.5 text-[hsl(38_20%_82%)]">
                  {content.openingHours.map((h, i) => (
                    <li key={i} className="flex justify-between gap-4">
                      <span>{h.day}</span>
                      <span className="text-white">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {content.socialLinks?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {content.socialLinks.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-[hsl(38_30%_92%)] hover:bg-white/10">
                    <LinkIcon className="h-3.5 w-3.5" /> {s.label}
                  </a>
                ))}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-[hsl(38_30%_92%)]">
                <Check className="h-3.5 w-3.5 text-[hsl(22_88%_65%)]" strokeWidth={3} />
                Maksuton arviokäynti
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-[hsl(38_30%_92%)]">
                <Shield className="h-3.5 w-3.5 text-[hsl(22_88%_65%)]" />
                Toiminnan vastuuvakuutus
              </span>
            </div>
          </div>

          <div>
            <div
              data-testid="quote-form"
              className="w-full max-w-none rounded-3xl border border-white/10 bg-white/[0.06] p-3 sm:p-5 backdrop-blur-sm"
            >
              <div className="px-2 sm:px-3 pt-2">
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  {q.title || "Tarjouspyyntö"}
                </h3>
                {q.lead && (
                  <p className="mt-1.5 text-sm text-[hsl(38_20%_82%)] leading-relaxed">
                    {q.lead}
                  </p>
                )}
              </div>

              {/* Valkoinen wrapper Jotformille — täysi leveys */}
              <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_-15px_rgba(0,0,0,0.35)]">
                <JotformEmbed formId={formId} testid="jotform-embed" />
              </div>

              <p className="mt-4 px-2 text-xs text-[hsl(38_20%_75%)] leading-relaxed">
                Jos lomake ei lataudu, voit avata tarjouspyyntölomakkeen{" "}
                <a
                  className="underline font-semibold text-[hsl(22_88%_70%)]"
                  href={`https://form.jotform.com/${formId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="jotform-fallback-link"
                >
                  tästä
                </a>
                {c.email ? (
                  <>
                    {" "}tai lähetä sähköpostia osoitteeseen{" "}
                    <a className="underline font-semibold text-[hsl(22_88%_70%)]" href={`mailto:${c.email}`}>
                      {c.email}
                    </a>
                    {c.phone ? ` — tai soita ${c.phone}.` : "."}
                  </>
                ) : (
                  "."
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value, href, testid }) {
  const inner = (
    <>
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-[hsl(38_30%_92%)]">{icon}</span>
      <span className="flex flex-col leading-tight">
        <span className="text-[11px] font-medium uppercase tracking-wider text-[hsl(38_20%_72%)]">{label}</span>
        <span className="text-base font-semibold text-white">{value}</span>
      </span>
    </>
  );
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {href ? <a href={href} className="flex items-center gap-3 hover:text-[hsl(22_88%_70%)]">{inner}</a> : inner}
    </div>
  );
}

/* -------------------- Footer -------------------- */
function Footer({ content }) {
  const c = content.contact || {};
  return (
    <footer data-testid="site-footer" className="bg-[hsl(148_45%_9%)] text-[hsl(38_20%_78%)] border-t border-white/5">
      <div className="container-page py-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(38_30%_95%)] text-[hsl(145_32%_18%)]">
              <TreePine className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <span className="text-white font-bold">{content.companyName}</span>
          </div>
          <p className="mt-3 text-sm leading-relaxed">
            Pihapuiden kaadot, ongelmapuiden poistot ja myrskytuhojen raivaukset Lempäälästä koko Pirkanmaan alueelle.
          </p>
        </div>
        <div className="text-sm leading-relaxed">
          <div className="text-white font-semibold mb-2">Yhteystiedot</div>
          {c.phone && <a href={c.phoneUrl} className="block hover:text-white">Puh. {c.phone}</a>}
          {c.email && <a href={`mailto:${c.email}`} className="block hover:text-white">{c.email}</a>}
          {c.address && <div className="mt-1">Kotipaikka: {c.address}</div>}
          {c.vatId && <div>Y-tunnus: {c.vatId}</div>}
        </div>
        <div className="text-sm leading-relaxed">
          <div className="text-white font-semibold mb-2">Linkit</div>
          {NAV_ITEMS.map((n) => (
            <a key={n.href} href={n.href} className="block hover:text-white">{n.label}</a>
          ))}
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="container-page py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-[hsl(38_20%_65%)]">
          <div>© {new Date().getFullYear()} {content.companyName}. Kaikki oikeudet pidätetään.</div>
          <div>Rakennettu paikallisesti · Lempäälä, Pirkanmaa</div>
        </div>
      </div>
    </footer>
  );
}

/* -------------------- Mobile Sticky CTA -------------------- */
function MobileStickyCTA({ content }) {
  const c = content.contact || {};
  return (
    <div data-testid="mobile-sticky-cta" className="lg:hidden fixed bottom-0 left-0 right-0 z-30 mobile-cta-shadow bg-[hsl(38_30%_97%)]/95 backdrop-blur-md border-t border-[hsl(38_20%_86%)]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="grid grid-cols-2 gap-2 p-2.5">
        <a href={c.phoneUrl || "#"} data-testid="sticky-call-btn" className="flex items-center justify-center gap-2 rounded-full bg-[hsl(145_32%_18%)] text-white px-4 py-3 text-sm font-semibold">
          <Phone className="h-4 w-4" /> Soita
        </a>
        <a href="#yhteystiedot" data-testid="sticky-quote-btn" className="flex items-center justify-center gap-2 rounded-full bg-[hsl(22_88%_52%)] text-white px-4 py-3 text-sm font-semibold">
          <Mail className="h-4 w-4" /> Tarjouspyyntö
        </a>
      </div>
    </div>
  );
}

/* -------------------- App -------------------- */
function App() {
  const [content, setContent] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    fetch("/content/site.json", { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setContent(mergeContent(FALLBACK, data));
        }
      })
      .catch(() => { /* keep fallback if fetch fails */ });
    return () => { cancelled = true; };
  }, []);

  // Update <title> and <meta description> when SEO fields change
  useEffect(() => {
    if (content.seo?.title) document.title = content.seo.title;
    if (content.seo?.description) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", content.seo.description);
    }
  }, [content.seo]);

  return (
    <div className="App min-h-screen bg-[hsl(38_30%_97%)] text-[hsl(145_25%_14%)]">
      <a href="#top" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 bg-[hsl(22_88%_52%)] text-white px-3 py-2 rounded-md text-sm">
        Siirry sisältöön
      </a>
      <Header content={content} />
      <main id="main">
        <Hero content={content} />
        <Services content={content} />
        <Steps content={content} />
        <About content={content} />
        <Area content={content} />
        <Faq content={content} />
        <Projects content={content} />
        <Testimonials content={content} />
        <Contact content={content} />
      </main>
      <Footer content={content} />
      <div className="h-16 lg:hidden" aria-hidden="true" />
      <MobileStickyCTA content={content} />
    </div>
  );
}

export default App;
